import { cloneDeep, pull, pullAll } from 'lodash';
import { IHistoryItem } from '@/framework';
import { Framework } from '@/framework/instance';
import { traverseTree } from '@/utils/tree-kit';
import { IGroupLayerProps, ILayerProps, LayerType } from '@/version-types';
import { RealEditMerge } from '../layer/real-edit-merge';
import { HistoryType, IHistoryMergeData, IHistoryPackData, IHistoryUnpackData } from './type';

export class GroupHistory extends Framework {
    private realEditMerge: RealEditMerge;

    constructor(identity: string) {
        super(identity);
        this.realEditMerge = new RealEditMerge(this.framework);
        this.registerPackHistory();
        this.registerUnpackHistory();
        this.registerMergeHistory();
    }

    /** 合并 */
    private registerMergeHistory() {
        this.framework.history.register(HistoryType.MERGE, {
            onRedoUndo: (historyItem: IHistoryItem<IHistoryMergeData>, isRedo: boolean, isUndo: boolean) => {
                if (historyItem.isDirty) return;
                const { delData, insertData } = historyItem.data.data;
                const { oldLayer, oldLayerIndex, childIds, childIndexes } = delData.item;
                const isExtraGroup = this.framework.checkIsExtraGroup(oldLayer);
                const originIndex = historyItem.originIndex;
                const originIds = this.framework.getRenderIds(originIndex);
                const flatLayersMap = this.framework.getFlatLayersMap();
                if (isRedo) {
                    const delIds = [...childIds, oldLayer.id];
                    pullAll(originIds, delIds);
                    delIds.forEach((id) => {
                        flatLayersMap.delete(id);
                    });
                    this.framework.stage.addOrUpdate(insertData, true, originIndex);
                    this.framework.operate.setActiveLayer(insertData.id);
                }
                if (isUndo) {
                    this.framework.operate.delLayer(insertData.id);
                    pullAll(originIds, [...childIds, oldLayer.id]);
                    if (isExtraGroup) {
                        for (let i = 0; i < childIds.length; i++) {
                            const id = childIds[i];
                            const index = childIndexes[i];
                            originIds.splice(index, 0, id);
                        }
                    }
                    originIds.splice(oldLayerIndex, 0, oldLayer.id);
                    this.framework.stage.addOrUpdate(oldLayer, true, originIndex);
                    this.framework.operate.setActiveLayer(oldLayer.id);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryMergeData>) => {
                const originIds = this.framework.getRenderIdsWithoutExtraGroup(historyItem.originIndex);
                return {
                    message: [originIds, historyItem.originIndex, historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryMergeData>) => {
                const originIds = this.framework.getRenderIdsWithoutExtraGroup(historyItem.originIndex);
                return {
                    message: [originIds, historyItem.originIndex, historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (historyItem: IHistoryItem<IHistoryMergeData>, isRedo, isUndo, store) => {
                if (isUndo) {
                    historyItem.isDirty = this.framework.checkLayerIsDirty({
                        originLayer: historyItem.data.data.insertData,
                    });
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        const originLayer = historyItem.data.data.delData.item.oldLayer;
                        const latestIsExtraGroup = this.framework.checkIsExtraGroup(originLayer);
                        const latestIsFormalGroup = this.framework.checkIsFormalGroup(originLayer);
                        historyItem.isDirty = this.framework.checkLayerIsDirty({
                            originLayer,
                            latestIsExtraGroup,
                            latestIsFormalGroup,
                        });
                    }
                }
            },
            onMessage: (message: [string[], number, IHistoryMergeData], isRedo, isUndo, isWrite) => {
                const [originIds, originIndex, mergeData] = message;
                const { delData, insertData } = mergeData;
                const { oldLayer, childIds } = delData.item;
                const currentOriginIds = this.framework.getRenderIds(originIndex);
                currentOriginIds.length = 0;
                currentOriginIds.push(...originIds);

                if (isRedo || isWrite) {
                    this.checkIsDirtyActiveInfo(oldLayer);
                    const delIds = [...childIds, oldLayer.id];
                    const flatLayersMap = this.framework.getFlatLayersMap();
                    delIds.forEach((id) => {
                        flatLayersMap.delete(id);
                    });
                    this.framework.stage.addOrUpdate(insertData, false, originIndex);
                }
                if (isUndo) {
                    this.checkIsDirtyActiveInfo(insertData);
                    // pull(currentOriginIds, insertData.id);
                    this.framework.getFlatLayersMap().delete(insertData.id);

                    const isExtraGroup = this.framework.checkIsExtraGroup(oldLayer);
                    if (isExtraGroup) {
                        // pull(currentOriginIds, oldLayer.id);
                        this.framework.getFlatLayersMap().delete(oldLayer.id);
                        for (let i = 0; i < oldLayer.children?.length; i++) {
                            const layer = oldLayer.children[i];
                            layer.parentId = undefined;
                            this.framework.stage.addOrUpdate(layer, false, originIndex);
                        }
                    } else {
                        this.framework.stage.addOrUpdate(oldLayer, false, originIndex);
                    }
                }
                this.framework.setRerender(originIndex);
                this.framework.rerender();
            },
        });
    }

    /** 成组 */
    private registerPackHistory() {
        this.framework.history.register(HistoryType.PACK, {
            onRedoUndo: (historyItem: IHistoryItem<IHistoryPackData>, isRedo: boolean, isUndo: boolean) => {
                const { item } = historyItem.data.data;
                const { childIndexes, childIds, layer, layerIndex } = item;
                historyItem.isDirty = this.framework.checkLayerIsDirty({
                    originLayer: layer,
                    latestIsExtraGroup: isRedo,
                    latestIsFormalGroup: isUndo,
                });
                if (historyItem.isDirty) return;
                this.framework.operate.delActiveLayer();
                const originIndex = historyItem.originIndex;
                const originIds = this.framework.getRenderIds(historyItem.originIndex);
                if (isRedo) {
                    pullAll(originIds, [...childIds, layer.id]);
                    originIds.splice(layerIndex, 0, layer.id);
                }
                if (isUndo) {
                    pull(originIds, layer.id);
                    for (let i = 0; i < childIndexes.length; i++) {
                        const index = childIndexes[i];
                        const id = childIds[i];
                        originIds.splice(index, 0, id);
                    }
                    originIds.push(layer.id);
                }
                this.resetGroupChild(layer);
                (layer.data as IGroupLayerProps).isExtraGroup = isUndo;
                this.framework.stage.addOrUpdate(layer, true, originIndex);
                this.framework.operate.setActiveLayer(layer.id);
            },
            onMerge: this.realEditMerge.merge.bind(this),
            onAfterWrite: this.getGroupMessage.bind(this),
            onAfterRedoUndo: this.getGroupMessage.bind(this),
            onMessage: this.processGroupMessage.bind(this),
        });
    }

    /** 解组 */
    private registerUnpackHistory() {
        this.framework.history.register(HistoryType.UNPACK, {
            onRedoUndo: (historyItem: IHistoryItem<IHistoryUnpackData>, isRedo: boolean, isUndo: boolean) => {
                const { item } = historyItem.data.data;
                const { layer, layerIndex } = item;
                historyItem.isDirty = this.framework.checkLayerIsDirty({
                    originLayer: layer,
                    latestIsExtraGroup: isUndo,
                    latestIsFormalGroup: isRedo,
                });
                // console.log('ot unpack onRedoUndo', { isDirty: historyItem.isDirty, isRedo, isUndo });
                if (historyItem.isDirty) return;
                this.framework.operate.delActiveLayer();
                const originIndex = historyItem.originIndex;
                const originIds = this.framework.getRenderIds(originIndex);
                const childIds: string[] = (layer.children || []).map((v) => v.id);
                if (isRedo) {
                    pull(originIds, layer.id);
                    originIds.splice(layerIndex, 0, ...childIds);
                    originIds.push(layer.id);
                    layer.lock = false;
                }
                if (isUndo) {
                    pullAll(originIds, [...childIds, layer.id]);
                    originIds.splice(layerIndex, 0, layer.id);
                }
                this.resetGroupChild(layer);
                (layer.data as IGroupLayerProps).isExtraGroup = isRedo;
                this.framework.stage.addOrUpdate(layer, true, originIndex);
                this.framework.operate.setActiveLayer(layer.id);
            },
            onMerge: this.realEditMerge.merge.bind(this),
            onAfterWrite: this.getGroupMessage.bind(this),
            onAfterRedoUndo: this.getGroupMessage.bind(this),
            onMessage: this.processGroupMessage.bind(this),
        });
    }

    /** 重置组的子图层 */
    private resetGroupChild(layer: ILayerProps) {
        const newChildren: ILayerProps[] = [];
        layer.children?.forEach((item) => {
            const childLayer = this.framework.getLayer(item.id, true);
            if (childLayer) {
                childLayer.parentId = layer.id;
                newChildren.push(childLayer);
            }
        });
        layer.children = newChildren;
    }

    /** 处理同步的数据 */
    private getGroupMessage(historyItem: IHistoryItem<IHistoryPackData | IHistoryUnpackData>) {
        return {
            message: [historyItem.originIndex, historyItem.data.data.item.layer],
        };
    }

    private checkIsDirtyActiveInfo(layer: ILayerProps) {
        const activeInfo = this.framework.operate.getActiveInfo();
        let isDirtyActiveInfo = false;
        if (activeInfo) {
            // 激活图层是临时组，并且包含了组的图层，则删除激活图层
            const activeLayer = this.framework.getLayer(activeInfo.info.id);
            if (activeLayer) {
                if (this.framework.checkIsExtraGroup(activeLayer)) {
                    const activeIds: Set<string> = new Set();
                    traverseTree(activeLayer, (node) => {
                        activeIds.add(node.id);
                    });
                    traverseTree(layer, (node) => {
                        if (activeIds.has(node.id)) {
                            isDirtyActiveInfo = true;
                        }
                    });
                } else {
                    traverseTree(layer, (node) => {
                        if (node.id === activeInfo.info.id || node.id === activeInfo.info.pickId) {
                            isDirtyActiveInfo = true;
                        }
                    });
                }
            }
        }

        if (isDirtyActiveInfo) {
            this.framework.operate.delActiveLayer();
        }

        return {
            isDirtyActiveInfo,
            activeInfo,
        };
    }

    /** 处理组的同步消息 */
    private processGroupMessage(message: [number, ILayerProps]) {
        const [originIndex, layer] = message;
        const { activeInfo } = this.checkIsDirtyActiveInfo(layer);
        const currentOriginIds = this.framework.getRenderIds(originIndex);

        if (this.framework.checkIsExtraGroup(layer)) {
            let layerIndex = currentOriginIds.indexOf(layer.id);
            if (layerIndex === -1) {
                layerIndex = currentOriginIds.length - 1;
            }
            this.framework.flatLayersMap.delete(layer.id);
            const childLayers: ILayerProps[] = [];
            const childIds: string[] = [];
            console.log('ot unpack', cloneDeep(layer));

            layer.children?.forEach((item) => {
                item.parentId = undefined;
                this.framework.stage.addOrUpdate(item, false, originIndex);
                childLayers.push(item);
                childIds.push(item.id);
            });
            pullAll(currentOriginIds, [...childIds, layer.id]);
            currentOriginIds.splice(layerIndex, 0, ...childIds);
            this.framework.setRerender(originIndex);
            this.framework.rerender();
            this.framework.createFontFamilySubset(childLayers, true);
            // if (!isDirtyActiveInfo && activeInfo?.info.id === layer.id) {
            //     if (activeInfo.info.pickId) {
            //         this.framework.operate.setActiveLayer(activeInfo.info.pickId);
            //     } else {
            //         this.framework.operate.delActiveLayer();
            //     }
            // }
        } else {
            const childIds: string[] = [];
            traverseTree(layer, (node) => {
                childIds.push(node.id);
            });
            pullAll(currentOriginIds, childIds);
            this.framework.stage.addOrUpdate(layer, true, originIndex);
            this.framework.createFontFamilySubset([layer], true);
            // if (!isDirtyActiveInfo && activeInfo?.info.id) {
            //     const wrapLayer = this.framework.getWrapLayer(activeInfo.info.id);
            //     if (wrapLayer?.id === layer.id) {
            //         if (activeInfo.info.layerType === LayerType.GROUP_LAYER && !activeInfo.info.pickId) {
            //             this.framework.operate.setActiveLayer(layer.id);
            //         } else {
            //             this.framework.operate.setActiveLayer(activeInfo.info.pickId || activeInfo.info.id);
            //         }
            //     }
            // }
        }
        if (activeInfo) {
            const activeId = activeInfo.info.pickId || activeInfo.info.id;
            this.framework.operate.setActiveLayer(activeId);
        }
    }
}
